import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";

export interface GeoAddressParts {
  street: string | null;
  district: string | null;
  city: string | null;
  stateLong: string | null;
  stateShort: string | null;
  postalCode: string | null;
}

interface GeoAddressComponent {
  long_name?: string;
  short_name?: string;
  types?: string[];
}

@Injectable({
  providedIn: "root",
})
export class GoogleMapsService {
  private apiKey = "AIzaSyAWHf8YuyJtv42nFtws9Rj4cGmut3S-yTo";
  constructor(private http: HttpClient) {}

  getGeoCode(address: string, countryCode?: string | null): Observable<any> {
    const params = new URLSearchParams({
      address,
      key: this.apiKey,
    });
    if (countryCode) {
      params.set("components", `country:${countryCode.toUpperCase()}`);
    }
    const url = `https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`;
    return this.http.get<any>(url);
  }

  reverseGeocode(lat: number, lng: number): Observable<string | null> {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${this.apiKey}`;
    return this.http.get<any>(url).pipe(
      map((response) => this.extractCityName(response?.results ?? [])),
      catchError(() => of(null)),
    );
  }

  getMapsUrl(lat: number, lng: number): string {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  extractAddressParts(result?: {
    address_components?: GeoAddressComponent[];
  } | null): GeoAddressParts {
    const components = result?.address_components ?? [];
    const find = (...types: string[]) =>
      components.find((component) =>
        types.some((type) => component.types?.includes(type)),
      );

    const route = find("route");
    const district = find("sublocality", "sublocality_level_1", "neighborhood");
    const city = find("locality", "administrative_area_level_2");
    const state = find("administrative_area_level_1");
    const postal = find("postal_code");

    return {
      street: route?.long_name ?? null,
      district: district?.long_name ?? null,
      city: city?.long_name ?? null,
      stateLong: state?.long_name ?? null,
      stateShort: state?.short_name ?? null,
      postalCode: postal?.long_name ?? postal?.short_name ?? null,
    };
  }

  private extractCityName(results: any[]): string | null {
    const preferredTypes = ["locality", "administrative_area_level_2"];
    for (const type of preferredTypes) {
      for (const result of results) {
        const components: any[] = result?.address_components ?? [];
        const match = components.find((c) => c?.types?.includes(type));
        if (match?.long_name) {
          return match.long_name as string;
        }
      }
    }
    return null;
  }
}
